import { __rest, __extends, __assign } from 'tslib';
import { pipe, angle, degreesToRadians, distance, isPoint3D, isPoint, mix, pointFromVector, progress, radiansToDegrees, smoothFrame, velocityPerFrame, velocityPerSecond, mixColor, mixComplex, clamp, applyOffset, conditional, interpolate, mixArray, smooth, snap, springForce, springForceExpo, springForceLinear, wrap } from '@popmotion/popcorn';
import sync, { getFrameData, cancelSync } from 'framesync';
import * as styleValueTypes from 'style-value-types';
import { color, complex, percent, degrees, vh, vw, px, number } from 'style-value-types';
export { styleValueTypes as valueTypes };
import * as easing from '@popmotion/easing';
import { linear, createReversedEasing, easeOut, easeInOut } from '@popmotion/easing';
export { easing };
import styler from 'stylefire';
export { default as styler } from 'stylefire';
import { warning } from 'hey-listen';
var Chainable = /*#__PURE__*/function () {
  function Chainable(props) {
    if (props === void 0) {
      props = {};
    }
    this.props = props;
  }
  Chainable.prototype.applyMiddleware = function (middleware) {
    return this.create(__assign({}, this.props, {
      middleware: this.props.middleware ? [middleware].concat(this.props.middleware) : [middleware]
    }));
  };
  Chainable.prototype.pipe = function () {
    var funcs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
      funcs[_i] = arguments[_i];
    }
    var pipedUpdate = funcs.length === 1 ? funcs[0] : pipe.apply(void 0, funcs);
    return this.applyMiddleware(function (update) {
      return function (v) {
        return update(pipedUpdate(v));
      };
    });
  };
  Chainable.prototype.while = function (predicate) {
    return this.applyMiddleware(function (update, complete) {
      return function (v) {
        return predicate(v) ? update(v) : complete();
      };
    });
  };
  Chainable.prototype.filter = function (predicate) {
    return this.applyMiddleware(function (update) {
      return function (v) {
        return predicate(v) && update(v);
      };
    });
  };
  return Chainable;
}();
var Observer = /*#__PURE__*/function () {
  function Observer(_a, observer) {
    var middleware = _a.middleware,
      onComplete = _a.onComplete;
    var _this = this;
    this.isActive = true;
    this.update = function (v) {
      if (_this.observer.update) _this.updateObserver(v);
    };
    this.complete = function () {
      if (_this.observer.complete && _this.isActive) _this.observer.complete();
      if (_this.onComplete) _this.onComplete();
      _this.isActive = false;
    };
    this.error = function (err) {
      if (_this.observer.error && _this.isActive) _this.observer.error(err);
      _this.isActive = false;
    };
    this.observer = observer;
    this.updateObserver = function (v) {
      return observer.update(v);
    };
    this.onComplete = onComplete;
    if (observer.update && middleware && middleware.length) {
      middleware.forEach(function (m) {
        return _this.updateObserver = m(_this.updateObserver, _this.complete);
      });
    }
  }
  return Observer;
}();
var createObserver = function createObserver(observerCandidate, _a, onComplete) {
  var middleware = _a.middleware;
  if (typeof observerCandidate === 'function') {
    return new Observer({
      middleware: middleware,
      onComplete: onComplete
    }, {
      update: observerCandidate
    });
  } else {
    return new Observer({
      middleware: middleware,
      onComplete: onComplete
    }, observerCandidate);
  }
};
var Action = /*#__PURE__*/function (_super) {
  __extends(Action, _super);
  function Action() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  Action.prototype.create = function (props) {
    return new Action(props);
  };
  Action.prototype.start = function (observerCandidate) {
    if (observerCandidate === void 0) {
      observerCandidate = {};
    }
    var isComplete = false;
    var subscription = {
      stop: function stop() {
        return undefined;
      }
    };
    var _a = this.props,
      init = _a.init,
      observerProps = __rest(_a, ["init"]);
    var observer = createObserver(observerCandidate, observerProps, function () {
      isComplete = true;
      subscription.stop();
    });
    var api = init(observer);
    subscription = api ? __assign({}, subscription, api) : subscription;
    if (observerCandidate.registerParent) {
      observerCandidate.registerParent(subscription);
    }
    if (isComplete) subscription.stop();
    return subscription;
  };
  return Action;
}(Chainable);
var action = function action(init) {
  return new Action({
    init: init
  });
};
var BaseMulticast = /*#__PURE__*/function (_super) {
  __extends(BaseMulticast, _super);
  function BaseMulticast() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.subscribers = [];
    return _this;
  }
  BaseMulticast.prototype.complete = function () {
    this.subscribers.forEach(function (subscriber) {
      return subscriber.complete();
    });
  };
  BaseMulticast.prototype.error = function (err) {
    this.subscribers.forEach(function (subscriber) {
      return subscriber.error(err);
    });
  };
  BaseMulticast.prototype.update = function (v) {
    for (var i = 0; i < this.subscribers.length; i++) {
      this.subscribers[i].update(v);
    }
  };
  BaseMulticast.prototype.subscribe = function (observerCandidate) {
    var _this = this;
    var observer = createObserver(observerCandidate, this.props);
    this.subscribers.push(observer);
    var subscription = {
      unsubscribe: function unsubscribe() {
        var index = _this.subscribers.indexOf(observer);
        if (index !== -1) _this.subscribers.splice(index, 1);
      }
    };
    return subscription;
  };
  BaseMulticast.prototype.stop = function () {
    if (this.parent) this.parent.stop();
  };
  BaseMulticast.prototype.registerParent = function (subscription) {
    this.stop();
    this.parent = subscription;
  };
  return BaseMulticast;
}(Chainable);
var Multicast = /*#__PURE__*/function (_super) {
  __extends(Multicast, _super);
  function Multicast() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  Multicast.prototype.create = function (props) {
    return new Multicast(props);
  };
  return Multicast;
}(BaseMulticast);
var multicast = function multicast() {
  return new Multicast();
};
var stepProgress = function stepProgress(steps, progress$$1) {
  var segment = 1 / (steps - 1);
  var subsegment = 1 / (2 * (steps - 1));
  var percentProgressOfTarget = Math.min(progress$$1, 1);
  var subsegmentProgressOfTarget = percentProgressOfTarget / subsegment;
  var segmentProgressOfTarget = Math.floor((subsegmentProgressOfTarget + 1) / 2);
  return segmentProgressOfTarget * segment;
};
var calc = /*#__PURE__*/Object.freeze({
  angle: angle,
  degreesToRadians: degreesToRadians,
  distance: distance,
  isPoint3D: isPoint3D,
  isPoint: isPoint,
  dilate: mix,
  getValueFromProgress: mix,
  pointFromAngleAndDistance: pointFromVector,
  getProgressFromValue: progress,
  radiansToDegrees: radiansToDegrees,
  smooth: smoothFrame,
  speedPerFrame: velocityPerFrame,
  speedPerSecond: velocityPerSecond,
  stepProgress: stepProgress
});
var isValueList = function isValueList(v) {
  return Array.isArray(v);
};
var isSingleValue = function isSingleValue(v) {
  var typeOfV = typeof v;
  return typeOfV === 'string' || typeOfV === 'number';
};
var ValueReaction = /*#__PURE__*/function (_super) {
  __extends(ValueReaction, _super);
  function ValueReaction(props) {
    var _this = _super.call(this, props) || this;
    _this.scheduleVelocityCheck = function () {
      return sync.postRender(_this.velocityCheck);
    };
    _this.velocityCheck = function (_a) {
      var timestamp = _a.timestamp;
      if (timestamp !== _this.lastUpdated) {
        _this.prev = _this.current;
      }
    };
    _this.prev = _this.current = props.value || 0;
    if (isSingleValue(_this.current)) {
      _this.updateCurrent = function (v) {
        return _this.current = v;
      };
      _this.getVelocityOfCurrent = function () {
        return _this.getSingleVelocity(_this.current, _this.prev);
      };
    } else if (isValueList(_this.current)) {
      _this.updateCurrent = function (v) {
        return _this.current = v.slice();
      };
      _this.getVelocityOfCurrent = function () {
        return _this.getListVelocity();
      };
    } else {
      _this.updateCurrent = function (v) {
        _this.current = {};
        for (var key in v) {
          if (v.hasOwnProperty(key)) {
            _this.current[key] = v[key];
          }
        }
      };
      _this.getVelocityOfCurrent = function () {
        return _this.getMapVelocity();
      };
    }
    if (props.initialSubscription) _this.subscribe(props.initialSubscription);
    return _this;
  }
  ValueReaction.prototype.create = function (props) {
    return new ValueReaction(props);
  };
  ValueReaction.prototype.get = function () {
    return this.current;
  };
  ValueReaction.prototype.getVelocity = function () {
    return this.getVelocityOfCurrent();
  };
  ValueReaction.prototype.update = function (v) {
    _super.prototype.update.call(this, v);
    this.prev = this.current;
    this.updateCurrent(v);
    var _a = getFrameData(),
      delta = _a.delta,
      timestamp = _a.timestamp;
    this.timeDelta = delta;
    this.lastUpdated = timestamp;
    sync.postRender(this.scheduleVelocityCheck);
  };
  ValueReaction.prototype.subscribe = function (observerCandidate) {
    var sub = _super.prototype.subscribe.call(this, observerCandidate);
    this.subscribers[this.subscribers.length - 1].update(this.current);
    return sub;
  };
  ValueReaction.prototype.getSingleVelocity = function (current, prev) {
    return typeof current === 'number' && typeof prev === 'number' ? velocityPerSecond(current - prev, this.timeDelta) : velocityPerSecond(parseFloat(current) - parseFloat(prev), this.timeDelta) || 0;
  };
  ValueReaction.prototype.getListVelocity = function () {
    var _this = this;
    return this.current.map(function (c, i) {
      return _this.getSingleVelocity(c, _this.prev[i]);
    });
  };
  ValueReaction.prototype.getMapVelocity = function () {
    var velocity = {};
    for (var key in this.current) {
      if (this.current.hasOwnProperty(key)) {
        velocity[key] = this.getSingleVelocity(this.current[key], this.prev[key]);
      }
    }
    return velocity;
  };
  return ValueReaction;
}(BaseMulticast);
var value = function value(_value, initialSubscription) {
  return new ValueReaction({
    value: _value,
    initialSubscription: initialSubscription
  });
};
var multi = function multi(_a) {
  var getCount = _a.getCount,
    getFirst = _a.getFirst,
    getOutput = _a.getOutput,
    mapApi = _a.mapApi,
    setProp = _a.setProp,
    startActions = _a.startActions;
  return function (actions) {
    return action(function (_a) {
      var update = _a.update,
        _complete = _a.complete,
        error = _a.error;
      var numActions = getCount(actions);
      var output = getOutput();
      var updateOutput = function updateOutput() {
        return update(output);
      };
      var numCompletedActions = 0;
      var subs = startActions(actions, function (a, name) {
        var hasCompleted = false;
        return a.start({
          complete: function complete() {
            if (!hasCompleted) {
              hasCompleted = true;
              numCompletedActions++;
              if (numCompletedActions === numActions) sync.update(_complete);
            }
          },
          error: error,
          update: function update(v) {
            setProp(output, name, v);
            sync.update(updateOutput, false, true);
          }
        });
      });
      return Object.keys(getFirst(subs)).reduce(function (api, methodName) {
        api[methodName] = mapApi(subs, methodName);
        return api;
      }, {});
    });
  };
};
var composite = /*#__PURE__*/multi({
  getOutput: function getOutput() {
    return {};
  },
  getCount: function getCount(subs) {
    return Object.keys(subs).length;
  },
  getFirst: function getFirst(subs) {
    return subs[Object.keys(subs)[0]];
  },
  mapApi: function mapApi(subs, methodName) {
    return function () {
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      return Object.keys(subs).reduce(function (output, propKey) {
        var _a;
        if (subs[propKey][methodName]) {
          args[0] && args[0][propKey] !== undefined ? output[propKey] = subs[propKey][methodName](args[0][propKey]) : output[propKey] = (_a = subs[propKey])[methodName].apply(_a, args);
        }
        return output;
      }, {});
    };
  },
  setProp: function setProp(output, name, v) {
    return output[name] = v;
  },
  startActions: function startActions(actions, starter) {
    return Object.keys(actions).reduce(function (subs, key) {
      subs[key] = starter(actions[key], key);
      return subs;
    }, {});
  }
});
var parallel = /*#__PURE__*/multi({
  getOutput: function getOutput() {
    return [];
  },
  getCount: function getCount(subs) {
    return subs.length;
  },
  getFirst: function getFirst(subs) {
    return subs[0];
  },
  mapApi: function mapApi(subs, methodName) {
    return function () {
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      return subs.map(function (sub, i) {
        if (sub[methodName]) {
          return Array.isArray(args[0]) ? sub[methodName](args[0][i]) : sub[methodName].apply(sub, args);
        }
      });
    };
  },
  setProp: function setProp(output, name, v) {
    return output[name] = v;
  },
  startActions: function startActions(actions, starter) {
    return actions.map(function (action, i) {
      return starter(action, i);
    });
  }
});
var parallel$1 = function parallel$1() {
  var actions = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    actions[_i] = arguments[_i];
  }
  return parallel(actions);
};
var createVectorTests = function createVectorTests(typeTests) {
  var testNames = Object.keys(typeTests);
  var isVectorProp = function isVectorProp(prop, key) {
    return prop !== undefined && !typeTests[key](prop);
  };
  var getVectorKeys = function getVectorKeys(props) {
    return testNames.reduce(function (vectorKeys, key) {
      if (isVectorProp(props[key], key)) vectorKeys.push(key);
      return vectorKeys;
    }, []);
  };
  var testVectorProps = function testVectorProps(props) {
    return props && testNames.some(function (key) {
      return isVectorProp(props[key], key);
    });
  };
  return {
    getVectorKeys: getVectorKeys,
    testVectorProps: testVectorProps
  };
};
var unitTypes = [px, percent, degrees, vh, vw];
var findUnitType = function findUnitType(prop) {
  return unitTypes.find(function (type) {
    return type.test(prop);
  });
};
var isUnitProp = function isUnitProp(prop) {
  return Boolean(findUnitType(prop));
};
var createAction = function createAction(action, props) {
  return action(props);
};
var reduceArrayValue = function reduceArrayValue(i) {
  return function (props, key) {
    props[key] = props[key][i];
    return props;
  };
};
var createArrayAction = function createArrayAction(action, props, vectorKeys) {
  var firstVectorKey = vectorKeys[0];
  var actionList = props[firstVectorKey].map(function (v, i) {
    var childActionProps = vectorKeys.reduce(reduceArrayValue(i), __assign({}, props));
    return getActionCreator(v)(action, childActionProps);
  });
  return parallel$1.apply(void 0, actionList);
};
var reduceObjectValue = function reduceObjectValue(key) {
  return function (props, propKey) {
    props[propKey] = props[propKey][key];
    return props;
  };
};
var createObjectAction = function createObjectAction(action, props, vectorKeys) {
  var firstVectorKey = vectorKeys[0];
  var actionMap = Object.keys(props[firstVectorKey]).reduce(function (map, key) {
    var childActionProps = vectorKeys.reduce(reduceObjectValue(key), __assign({}, props));
    map[key] = getActionCreator(props[firstVectorKey][key])(action, childActionProps);
    return map;
  }, {});
  return composite(actionMap);
};
var createUnitAction = function createUnitAction(action, _a) {
  var from = _a.from,
    to = _a.to,
    props = __rest(_a, ["from", "to"]);
  var unitType = findUnitType(from) || findUnitType(to);
  var transform = unitType.transform,
    parse = unitType.parse;
  return action(__assign({}, props, {
    from: typeof from === 'string' ? parse(from) : from,
    to: typeof to === 'string' ? parse(to) : to
  })).pipe(transform);
};
var createMixerAction = function createMixerAction(mixer) {
  return function (action, _a) {
    var from = _a.from,
      to = _a.to,
      props = __rest(_a, ["from", "to"]);
    return action(__assign({}, props, {
      from: 0,
      to: 1
    })).pipe(mixer(from, to));
  };
};
var createColorAction = /*#__PURE__*/createMixerAction(mixColor);
var createComplexAction = /*#__PURE__*/createMixerAction(mixComplex);
var createVectorAction = function createVectorAction(action, typeTests) {
  var _a = createVectorTests(typeTests),
    testVectorProps = _a.testVectorProps,
    getVectorKeys = _a.getVectorKeys;
  var vectorAction = function vectorAction(props) {
    var isVector = testVectorProps(props);
    if (!isVector) return action(props);
    var vectorKeys = getVectorKeys(props);
    var testKey = vectorKeys[0];
    var testProp = props[testKey];
    return getActionCreator(testProp)(action, props, vectorKeys);
  };
  return vectorAction;
};
var getActionCreator = function getActionCreator(prop) {
  if (typeof prop === 'number') {
    return createAction;
  } else if (Array.isArray(prop)) {
    return createArrayAction;
  } else if (isUnitProp(prop)) {
    return createUnitAction;
  } else if (color.test(prop)) {
    return createColorAction;
  } else if (complex.test(prop)) {
    return createComplexAction;
  } else if (typeof prop === 'object') {
    return createObjectAction;
  } else {
    return createAction;
  }
};
var decay = function decay(props) {
  if (props === void 0) {
    props = {};
  }
  return action(function (_a) {
    var complete = _a.complete,
      update = _a.update;
    var _b = props.velocity,
      velocity = _b === void 0 ? 0 : _b,
      _c = props.from,
      from = _c === void 0 ? 0 : _c,
      _d = props.power,
      power = _d === void 0 ? 0.8 : _d,
      _e = props.timeConstant,
      timeConstant = _e === void 0 ? 350 : _e,
      _f = props.restDelta,
      restDelta = _f === void 0 ? 0.5 : _f,
      modifyTarget = props.modifyTarget;
    var elapsed = 0;
    var amplitude = power * velocity;
    var idealTarget = Math.round(from + amplitude);
    var target = typeof modifyTarget === 'undefined' ? idealTarget : modifyTarget(idealTarget);
    var process = sync.update(function (_a) {
      var frameDelta = _a.delta;
      elapsed += frameDelta;
      var delta = -amplitude * Math.exp(-elapsed / timeConstant);
      var isMoving = delta > restDelta || delta < -restDelta;
      var current = isMoving ? target + delta : target;
      update(current);
      if (!isMoving) {
        cancelSync.update(process);
        complete();
      }
    }, true);
    return {
      stop: function stop() {
        return cancelSync.update(process);
      }
    };
  });
};
var vectorDecay = /*#__PURE__*/createVectorAction(decay, {
  from: number.test,
  modifyTarget: function modifyTarget(func) {
    return typeof func === 'function';
  },
  velocity: number.test
});
var spring = function spring(props) {
  if (props === void 0) {
    props = {};
  }
  return action(function (_a) {
    var update = _a.update,
      complete = _a.complete;
    var _b = props.velocity,
      velocity = _b === void 0 ? 0.0 : _b;
    var _c = props.from,
      from = _c === void 0 ? 0.0 : _c,
      _d = props.to,
      to = _d === void 0 ? 0.0 : _d,
      _e = props.stiffness,
      stiffness = _e === void 0 ? 100 : _e,
      _f = props.damping,
      damping = _f === void 0 ? 10 : _f,
      _g = props.mass,
      mass = _g === void 0 ? 1.0 : _g,
      _h = props.restSpeed,
      restSpeed = _h === void 0 ? 0.01 : _h,
      _j = props.restDelta,
      restDelta = _j === void 0 ? 0.01 : _j;
    var initialVelocity = velocity ? -(velocity / 1000) : 0.0;
    var t = 0;
    var delta = to - from;
    var position = from;
    var prevPosition = position;
    var process = sync.update(function (_a) {
      var timeDelta = _a.delta;
      t += timeDelta;
      var dampingRatio = damping / (2 * Math.sqrt(stiffness * mass));
      var angularFreq = Math.sqrt(stiffness / mass) / 1000;
      prevPosition = position;
      if (dampingRatio < 1) {
        var envelope = Math.exp(-dampingRatio * angularFreq * t);
        var expoDecay = angularFreq * Math.sqrt(1.0 - dampingRatio * dampingRatio);
        position = to - envelope * ((initialVelocity + dampingRatio * angularFreq * delta) / expoDecay * Math.sin(expoDecay * t) + delta * Math.cos(expoDecay * t));
      } else {
        var envelope = Math.exp(-angularFreq * t);
        position = to - envelope * (delta + (initialVelocity + angularFreq * delta) * t);
      }
      velocity = velocityPerSecond(position - prevPosition, timeDelta);
      var isBelowVelocityThreshold = Math.abs(velocity) <= restSpeed;
      var isBelowDisplacementThreshold = Math.abs(to - position) <= restDelta;
      if (isBelowVelocityThreshold && isBelowDisplacementThreshold) {
        position = to;
        update(position);
        cancelSync.update(process);
        complete();
      } else {
        update(position);
      }
    }, true);
    return {
      stop: function stop() {
        return cancelSync.update(process);
      }
    };
  });
};
var vectorSpring = /*#__PURE__*/createVectorAction(spring, {
  from: number.test,
  to: number.test,
  stiffness: number.test,
  damping: number.test,
  mass: number.test,
  velocity: number.test
});
var inertia = function inertia(_a) {
  var _b = _a.from,
    from = _b === void 0 ? 0 : _b,
    _c = _a.velocity,
    velocity = _c === void 0 ? 0 : _c,
    min = _a.min,
    max = _a.max,
    _d = _a.power,
    power = _d === void 0 ? 0.8 : _d,
    _e = _a.timeConstant,
    timeConstant = _e === void 0 ? 700 : _e,
    _f = _a.bounceStiffness,
    bounceStiffness = _f === void 0 ? 500 : _f,
    _g = _a.bounceDamping,
    bounceDamping = _g === void 0 ? 10 : _g,
    _h = _a.restDelta,
    restDelta = _h === void 0 ? 1 : _h,
    modifyTarget = _a.modifyTarget;
  return action(function (_a) {
    var update = _a.update,
      _complete2 = _a.complete;
    var current = value(from);
    var activeAnimation;
    var isSpring = false;
    var isLessThanMin = function isLessThanMin(v) {
      return min !== undefined && v <= min;
    };
    var isMoreThanMax = function isMoreThanMax(v) {
      return max !== undefined && v >= max;
    };
    var isOutOfBounds = function isOutOfBounds(v) {
      return isLessThanMin(v) || isMoreThanMax(v);
    };
    var isTravellingAwayFromBounds = function isTravellingAwayFromBounds(v, currentVelocity) {
      return isLessThanMin(v) && currentVelocity < 0 || isMoreThanMax(v) && currentVelocity > 0;
    };
    var startAnimation = function startAnimation(animation, next) {
      activeAnimation && activeAnimation.stop();
      activeAnimation = animation.start({
        update: function update(v) {
          return current.update(v);
        },
        complete: function complete() {
          if (next) {
            next();
            return;
          }
          _complete2();
        }
      });
    };
    var startSpring = function startSpring(props) {
      isSpring = true;
      startAnimation(vectorSpring(__assign({}, props, {
        to: isLessThanMin(props.from) ? min : max,
        stiffness: bounceStiffness,
        damping: bounceDamping,
        restDelta: restDelta
      })));
    };
    current.subscribe(function (v) {
      update(v);
      var currentVelocity = current.getVelocity();
      if (activeAnimation && !isSpring && isTravellingAwayFromBounds(v, currentVelocity)) {
        startSpring({
          from: v,
          velocity: currentVelocity
        });
      }
    });
    if (isOutOfBounds(from)) {
      startSpring({
        from: from,
        velocity: velocity
      });
    } else if (velocity !== 0) {
      var animation = vectorDecay({
        from: from,
        velocity: velocity,
        timeConstant: timeConstant,
        power: power,
        restDelta: isOutOfBounds(from) ? 20 : restDelta,
        modifyTarget: modifyTarget
      });
      startAnimation(animation, function () {
        var v = current.get();
        if (isOutOfBounds(v)) {
          startSpring({
            from: v,
            velocity: current.getVelocity()
          });
        } else {
          _complete2();
        }
      });
    } else {
      _complete2();
    }
    return {
      stop: function stop() {
        return activeAnimation && activeAnimation.stop();
      }
    };
  });
};
var index = /*#__PURE__*/createVectorAction(inertia, {
  from: number.test,
  velocity: number.test,
  min: number.test,
  max: number.test,
  damping: number.test,
  stiffness: number.test,
  modifyTarget: function modifyTarget(func) {
    return typeof func === 'function';
  }
});
var frame = function frame() {
  return action(function (_a) {
    var update = _a.update;
    var initialTime = 0;
    var process = sync.update(function (_a) {
      var timestamp = _a.timestamp;
      if (!initialTime) initialTime = timestamp;
      update(timestamp - initialTime);
    }, true, true);
    return {
      stop: function stop() {
        return cancelSync.update(process);
      }
    };
  });
};
var scrubber = function scrubber(_a) {
  var _b = _a.from,
    from = _b === void 0 ? 0 : _b,
    _c = _a.to,
    to = _c === void 0 ? 1 : _c,
    _d = _a.ease,
    ease = _d === void 0 ? linear : _d,
    _e = _a.reverseEase,
    reverseEase = _e === void 0 ? false : _e;
  if (reverseEase) {
    ease = createReversedEasing(ease);
  }
  return action(function (_a) {
    var update = _a.update;
    return {
      seek: function seek(progress$$1) {
        return update(progress$$1);
      }
    };
  }).pipe(ease, function (v) {
    return mix(from, to, v);
  });
};
var vectorScrubber = /*#__PURE__*/createVectorAction(scrubber, {
  ease: function ease(func) {
    return typeof func === 'function';
  },
  from: number.test,
  to: number.test
});
var clampProgress = /*#__PURE__*/clamp(0, 1);
var tween = function tween(props) {
  if (props === void 0) {
    props = {};
  }
  return action(function (_a) {
    var update = _a.update,
      complete = _a.complete;
    var _b = props.duration,
      duration = _b === void 0 ? 300 : _b,
      _c = props.ease,
      ease = _c === void 0 ? easeOut : _c,
      _d = props.flip,
      flip = _d === void 0 ? 0 : _d,
      _e = props.loop,
      loop = _e === void 0 ? 0 : _e,
      _f = props.yoyo,
      yoyo = _f === void 0 ? 0 : _f,
      _g = props.repeatDelay,
      repeatDelay = _g === void 0 ? 0 : _g;
    var _h = props.from,
      from = _h === void 0 ? 0 : _h,
      _j = props.to,
      to = _j === void 0 ? 1 : _j,
      _k = props.elapsed,
      elapsed = _k === void 0 ? 0 : _k,
      _l = props.flipCount,
      flipCount = _l === void 0 ? 0 : _l,
      _m = props.yoyoCount,
      yoyoCount = _m === void 0 ? 0 : _m,
      _o = props.loopCount,
      loopCount = _o === void 0 ? 0 : _o;
    var playhead = vectorScrubber({
      from: from,
      to: to,
      ease: ease
    }).start(update);
    var currentProgress = 0;
    var process;
    var _isActive = false;
    var reverseAnimation = function reverseAnimation(reverseEase) {
      if (reverseEase === void 0) {
        reverseEase = false;
      }
      var _a;
      _a = [to, from], from = _a[0], to = _a[1];
      playhead = vectorScrubber({
        from: from,
        to: to,
        ease: ease,
        reverseEase: reverseEase
      }).start(update);
    };
    var isTweenComplete = function isTweenComplete() {
      var isComplete = _isActive && elapsed > duration + repeatDelay;
      if (!isComplete) return false;
      if (isComplete && !loop && !flip && !yoyo) return true;
      elapsed = duration - (elapsed - repeatDelay);
      if (loop && loopCount < loop) {
        loopCount++;
        return false;
      } else if (flip && flipCount < flip) {
        flipCount++;
        reverseAnimation();
        return false;
      } else if (yoyo && yoyoCount < yoyo) {
        yoyoCount++;
        reverseAnimation(yoyoCount % 2 !== 0);
        return false;
      }
      return true;
    };
    var updateTween = function updateTween() {
      currentProgress = clampProgress(progress(0, duration, elapsed));
      playhead.seek(currentProgress);
    };
    var startTimer = function startTimer() {
      _isActive = true;
      process = sync.update(function (_a) {
        var delta = _a.delta;
        elapsed += delta;
        updateTween();
        if (isTweenComplete()) {
          cancelSync.update(process);
          complete && sync.update(complete, false, true);
        }
      }, true);
    };
    var stopTimer = function stopTimer() {
      _isActive = false;
      if (process) cancelSync.update(process);
    };
    startTimer();
    return {
      isActive: function isActive() {
        return _isActive;
      },
      getElapsed: function getElapsed() {
        return clamp(0, duration, elapsed);
      },
      getProgress: function getProgress() {
        return currentProgress;
      },
      stop: function stop() {
        stopTimer();
      },
      pause: function pause() {
        stopTimer();
        return this;
      },
      resume: function resume() {
        if (!_isActive) startTimer();
        return this;
      },
      seek: function seek(newProgress) {
        elapsed = mix(0, duration, newProgress);
        sync.update(updateTween, false, true);
        return this;
      },
      reverse: function reverse() {
        reverseAnimation();
        return this;
      }
    };
  });
};
var clampProgress$1 = /*#__PURE__*/clamp(0, 1);
var defaultEasings = function defaultEasings(values, easing$$1) {
  return values.map(function () {
    return easing$$1 || easeOut;
  }).splice(0, values.length - 1);
};
var defaultTimings = function defaultTimings(values) {
  var numValues = values.length;
  return values.map(function (value, i) {
    return i !== 0 ? i / (numValues - 1) : 0;
  });
};
var interpolateScrubbers = function interpolateScrubbers(input, scrubbers, update) {
  var rangeLength = input.length;
  var finalInputIndex = rangeLength - 1;
  var finalScrubberIndex = finalInputIndex - 1;
  var subs = scrubbers.map(function (scrub) {
    return scrub.start(update);
  });
  return function (v) {
    if (v <= input[0]) {
      subs[0].seek(0);
    }
    if (v >= input[finalInputIndex]) {
      subs[finalScrubberIndex].seek(1);
    }
    var i = 1;
    for (; i < rangeLength; i++) {
      if (input[i] > v || i === finalInputIndex) break;
    }
    var progressInRange = progress(input[i - 1], input[i], v);
    subs[i - 1].seek(clampProgress$1(progressInRange));
  };
};
var keyframes = function keyframes(_a) {
  var easings = _a.easings,
    _b = _a.ease,
    ease = _b === void 0 ? linear : _b,
    times = _a.times,
    values = _a.values,
    tweenProps = __rest(_a, ["easings", "ease", "times", "values"]);
  easings = Array.isArray(easings) ? easings : defaultEasings(values, easings);
  times = times || defaultTimings(values);
  var scrubbers = easings.map(function (easing$$1, i) {
    return vectorScrubber({
      from: values[i],
      to: values[i + 1],
      ease: easing$$1
    });
  });
  return tween(__assign({}, tweenProps, {
    ease: ease
  })).applyMiddleware(function (update) {
    return interpolateScrubbers(times, scrubbers, update);
  });
};
var physics = function physics(props) {
  if (props === void 0) {
    props = {};
  }
  return action(function (_a) {
    var complete = _a.complete,
      update = _a.update;
    var _b = props.acceleration,
      acceleration = _b === void 0 ? 0 : _b,
      _c = props.friction,
      friction = _c === void 0 ? 0 : _c,
      _d = props.velocity,
      velocity = _d === void 0 ? 0 : _d,
      springStrength = props.springStrength,
      to = props.to;
    var _e = props.restSpeed,
      restSpeed = _e === void 0 ? 0.001 : _e,
      _f = props.from,
      from = _f === void 0 ? 0 : _f;
    var current = from;
    var process = sync.update(function (_a) {
      var delta = _a.delta;
      var elapsed = Math.max(delta, 16);
      if (acceleration) velocity += velocityPerFrame(acceleration, elapsed);
      if (friction) velocity *= Math.pow(1 - friction, elapsed / 100);
      if (springStrength !== undefined && to !== undefined) {
        var distanceToTarget = to - current;
        velocity += distanceToTarget * velocityPerFrame(springStrength, elapsed);
      }
      current += velocityPerFrame(velocity, elapsed);
      update(current);
      var isComplete = restSpeed !== false && (!velocity || Math.abs(velocity) <= restSpeed);
      if (isComplete) {
        cancelSync.update(process);
        complete();
      }
    }, true);
    return {
      set: function set(v) {
        current = v;
        return this;
      },
      setAcceleration: function setAcceleration(v) {
        acceleration = v;
        return this;
      },
      setFriction: function setFriction(v) {
        friction = v;
        return this;
      },
      setSpringStrength: function setSpringStrength(v) {
        springStrength = v;
        return this;
      },
      setSpringTarget: function setSpringTarget(v) {
        to = v;
        return this;
      },
      setVelocity: function setVelocity(v) {
        velocity = v;
        return this;
      },
      stop: function stop() {
        return cancelSync.update(process);
      }
    };
  });
};
var vectorPhysics = /*#__PURE__*/createVectorAction(physics, {
  acceleration: number.test,
  friction: number.test,
  velocity: number.test,
  from: number.test,
  to: number.test,
  springStrength: number.test
});
var DEFAULT_DURATION = 300;
var flattenTimings = function flattenTimings(instructions) {
  var flatInstructions = [];
  var lastArg = instructions[instructions.length - 1];
  var isStaggered = typeof lastArg === 'number';
  var staggerDelay = isStaggered ? lastArg : 0;
  var segments = isStaggered ? instructions.slice(0, -1) : instructions;
  var numSegments = segments.length;
  var offset = 0;
  segments.forEach(function (item, i) {
    flatInstructions.push(item);
    if (i !== numSegments - 1) {
      var duration = item.duration || DEFAULT_DURATION;
      offset += staggerDelay;
      flatInstructions.push("-" + (duration - offset));
    }
  });
  return flatInstructions;
};
var flattenArrayInstructions = function flattenArrayInstructions(instructions, instruction) {
  Array.isArray(instruction) ? instructions.push.apply(instructions, flattenTimings(instruction)) : instructions.push(instruction);
  return instructions;
};
var convertDefToProps = function convertDefToProps(props, def, i) {
  var duration = props.duration,
    easings = props.easings,
    times = props.times,
    values = props.values;
  var numValues = values.length;
  var prevTimeTo = times[numValues - 1];
  var timeFrom = def.at === 0 ? 0 : def.at / duration;
  var timeTo = (def.at + def.duration) / duration;
  if (i === 0) {
    values.push(def.from);
    times.push(timeFrom);
  } else {
    if (prevTimeTo !== timeFrom) {
      if (def.from !== undefined) {
        values.push(values[numValues - 1]);
        times.push(timeFrom);
        easings.push(linear);
      }
      var from = def.from !== undefined ? def.from : values[numValues - 1];
      values.push(from);
      times.push(timeFrom);
      easings.push(linear);
    } else if (def.from !== undefined) {
      values.push(def.from);
      times.push(timeFrom);
      easings.push(linear);
    }
  }
  values.push(def.to);
  times.push(timeTo);
  easings.push(def.ease || easeInOut);
  return props;
};
var timeline = function timeline(instructions, _a) {
  var _b = _a === void 0 ? {} : _a,
    duration = _b.duration,
    elapsed = _b.elapsed,
    ease = _b.ease,
    loop = _b.loop,
    flip = _b.flip,
    yoyo = _b.yoyo;
  var playhead = 0;
  var calculatedDuration = 0;
  var flatInstructions = instructions.reduce(flattenArrayInstructions, []);
  var animationDefs = [];
  flatInstructions.forEach(function (instruction) {
    if (typeof instruction === 'string') {
      playhead += parseFloat(instruction);
    } else if (typeof instruction === 'number') {
      playhead = instruction;
    } else {
      var def = __assign({}, instruction, {
        at: playhead
      });
      def.duration = def.duration === undefined ? DEFAULT_DURATION : def.duration;
      animationDefs.push(def);
      playhead += def.duration;
      calculatedDuration = Math.max(calculatedDuration, def.at + def.duration);
    }
  });
  var tracks = {};
  var numDefs = animationDefs.length;
  for (var i = 0; i < numDefs; i++) {
    var def = animationDefs[i];
    var track = def.track;
    if (track === undefined) {
      throw new Error('No track defined');
    }
    if (!tracks.hasOwnProperty(track)) tracks[track] = [];
    tracks[track].push(def);
  }
  var trackKeyframes = {};
  for (var key in tracks) {
    if (tracks.hasOwnProperty(key)) {
      var keyframeProps = tracks[key].reduce(convertDefToProps, {
        duration: calculatedDuration,
        easings: [],
        times: [],
        values: []
      });
      trackKeyframes[key] = keyframes(__assign({}, keyframeProps, {
        duration: duration || calculatedDuration,
        ease: ease,
        elapsed: elapsed,
        loop: loop,
        yoyo: yoyo,
        flip: flip
      }));
    }
  }
  return composite(trackKeyframes);
};
var listen = function listen(element, events, options) {
  return action(function (_a) {
    var update = _a.update;
    var eventNames = events.split(' ').map(function (eventName) {
      element.addEventListener(eventName, update, options);
      return eventName;
    });
    return {
      stop: function stop() {
        return eventNames.forEach(function (eventName) {
          return element.removeEventListener(eventName, update, options);
        });
      }
    };
  });
};
var defaultPointerPos = function defaultPointerPos() {
  return {
    clientX: 0,
    clientY: 0,
    pageX: 0,
    pageY: 0,
    x: 0,
    y: 0
  };
};
var eventToPoint = function eventToPoint(e, point) {
  if (point === void 0) {
    point = defaultPointerPos();
  }
  point.clientX = point.x = e.clientX;
  point.clientY = point.y = e.clientY;
  point.pageX = e.pageX;
  point.pageY = e.pageY;
  return point;
};
var points = [/*#__PURE__*/defaultPointerPos()];
var isTouchDevice = false;
if (typeof document !== 'undefined') {
  var updatePointsLocation = function updatePointsLocation(_a) {
    var touches = _a.touches;
    isTouchDevice = true;
    var numTouches = touches.length;
    points.length = 0;
    for (var i = 0; i < numTouches; i++) {
      var thisTouch = touches[i];
      points.push(eventToPoint(thisTouch));
    }
  };
  listen(document, 'touchstart touchmove', {
    passive: true,
    capture: true
  }).start(updatePointsLocation);
}
var multitouch = function multitouch(_a) {
  var _b = _a === void 0 ? {} : _a,
    _c = _b.preventDefault,
    preventDefault = _c === void 0 ? true : _c,
    _d = _b.scale,
    scale = _d === void 0 ? 1.0 : _d,
    _e = _b.rotate,
    rotate = _e === void 0 ? 0.0 : _e;
  return action(function (_a) {
    var update = _a.update;
    var output = {
      touches: points,
      scale: scale,
      rotate: rotate
    };
    var initialDistance = 0.0;
    var initialRotation = 0.0;
    var isGesture = points.length > 1;
    if (isGesture) {
      var firstTouch = points[0],
        secondTouch = points[1];
      initialDistance = distance(firstTouch, secondTouch);
      initialRotation = angle(firstTouch, secondTouch);
    }
    var updatePoint = function updatePoint() {
      if (isGesture) {
        var firstTouch = points[0],
          secondTouch = points[1];
        var newDistance = distance(firstTouch, secondTouch);
        var newRotation = angle(firstTouch, secondTouch);
        output.scale = scale * (newDistance / initialDistance);
        output.rotate = rotate + (newRotation - initialRotation);
      }
      update(output);
    };
    var onMove = function onMove(e) {
      if (preventDefault || e.touches.length > 1) e.preventDefault();
      sync.update(updatePoint);
    };
    var updateOnMove = listen(document, 'touchmove', {
      passive: !preventDefault
    }).start(onMove);
    if (isTouchDevice) sync.update(updatePoint);
    return {
      stop: function stop() {
        cancelSync.update(updatePoint);
        updateOnMove.stop();
      }
    };
  });
};
var getIsTouchDevice = function getIsTouchDevice() {
  return isTouchDevice;
};
var point = /*#__PURE__*/defaultPointerPos();
var isMouseDevice = false;
if (typeof document !== 'undefined') {
  var updatePointLocation = function updatePointLocation(e) {
    isMouseDevice = true;
    eventToPoint(e, point);
  };
  listen(document, 'mousedown mousemove', true).start(updatePointLocation);
}
var mouse = function mouse(_a) {
  var _b = (_a === void 0 ? {} : _a).preventDefault,
    preventDefault = _b === void 0 ? true : _b;
  return action(function (_a) {
    var update = _a.update;
    var updatePoint = function updatePoint() {
      return update(point);
    };
    var onMove = function onMove(e) {
      if (preventDefault) e.preventDefault();
      sync.update(updatePoint);
    };
    var updateOnMove = listen(document, 'mousemove').start(onMove);
    if (isMouseDevice) sync.update(updatePoint);
    return {
      stop: function stop() {
        cancelSync.update(updatePoint);
        updateOnMove.stop();
      }
    };
  });
};
var getFirstTouch = function getFirstTouch(_a) {
  var firstTouch = _a[0];
  return firstTouch;
};
var pointer = function pointer(props) {
  if (props === void 0) {
    props = {};
  }
  return getIsTouchDevice() ? multitouch(props).pipe(function (_a) {
    var touches = _a.touches;
    return touches;
  }, getFirstTouch) : mouse(props);
};
var index$1 = function index$1(_a) {
  if (_a === void 0) {
    _a = {};
  }
  var x = _a.x,
    y = _a.y,
    props = __rest(_a, ["x", "y"]);
  if (x !== undefined || y !== undefined) {
    var applyXOffset_1 = applyOffset(x || 0);
    var applyYOffset_1 = applyOffset(y || 0);
    var delta_1 = {
      x: 0,
      y: 0
    };
    return pointer(props).pipe(function (point) {
      delta_1.x = applyXOffset_1(point.x);
      delta_1.y = applyYOffset_1(point.y);
      return delta_1;
    });
  } else {
    return pointer(props);
  }
};
var chain = function chain() {
  var actions = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    actions[_i] = arguments[_i];
  }
  return action(function (_a) {
    var update = _a.update,
      _complete3 = _a.complete;
    var i = 0;
    var current;
    var playCurrent = function playCurrent() {
      current = actions[i].start({
        complete: function complete() {
          i++;
          i >= actions.length ? _complete3() : playCurrent();
        },
        update: update
      });
    };
    playCurrent();
    return {
      stop: function stop() {
        return current && current.stop();
      }
    };
  });
};
var crossfade = function crossfade(a, b) {
  return action(function (observer) {
    var balance = 0;
    var fadable = parallel$1(a, b).start(__assign({}, observer, {
      update: function update(_a) {
        var va = _a[0],
          vb = _a[1];
        observer.update(mix(va, vb, balance));
      }
    }));
    return {
      setBalance: function setBalance(v) {
        return balance = v;
      },
      stop: function stop() {
        return fadable.stop();
      }
    };
  });
};
var delay = function delay(timeToDelay) {
  return action(function (_a) {
    var complete = _a.complete;
    var timeout = setTimeout(complete, timeToDelay);
    return {
      stop: function stop() {
        return clearTimeout(timeout);
      }
    };
  });
};
var merge = function merge() {
  var actions = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    actions[_i] = arguments[_i];
  }
  return action(function (observer) {
    var subs = actions.map(function (thisAction) {
      return thisAction.start(observer);
    });
    return {
      stop: function stop() {
        return subs.forEach(function (sub) {
          return sub.stop();
        });
      }
    };
  });
};
var schedule = function schedule(scheduler, schedulee) {
  return action(function (_a) {
    var _update = _a.update,
      complete = _a.complete;
    var latest;
    var schedulerSub = scheduler.start({
      update: function update() {
        return latest !== undefined && _update(latest);
      },
      complete: complete
    });
    var scheduleeSub = schedulee.start({
      update: function update(v) {
        return latest = v;
      },
      complete: complete
    });
    return {
      stop: function stop() {
        schedulerSub.stop();
        scheduleeSub.stop();
      }
    };
  });
};
var stagger = function stagger(actions, interval) {
  var intervalIsNumber = typeof interval === 'number';
  var actionsWithDelay = actions.map(function (a, i) {
    var timeToDelay = intervalIsNumber ? interval * i : interval(i);
    return chain(delay(timeToDelay), a);
  });
  return parallel$1.apply(void 0, actionsWithDelay);
};
var appendUnit = function appendUnit(unit) {
  return function (v) {
    return "" + v + unit;
  };
};
var steps = function steps(st, min, max) {
  if (min === void 0) {
    min = 0;
  }
  if (max === void 0) {
    max = 1;
  }
  return function (v) {
    var current = progress(min, max, v);
    return mix(min, max, stepProgress(st, current));
  };
};
var transformMap = function transformMap(childTransformers) {
  return function (v) {
    var output = __assign({}, v);
    for (var key in childTransformers) {
      if (childTransformers.hasOwnProperty(key)) {
        var childTransformer = childTransformers[key];
        output[key] = childTransformer(v[key]);
      }
    }
    return output;
  };
};
var transformers = /*#__PURE__*/Object.freeze({
  applyOffset: applyOffset,
  clamp: clamp,
  conditional: conditional,
  interpolate: interpolate,
  blendArray: mixArray,
  blendColor: mixColor,
  pipe: pipe,
  smooth: smooth,
  snap: snap,
  generateStaticSpring: springForce,
  nonlinearSpring: springForceExpo,
  linearSpring: springForceLinear,
  wrap: wrap,
  appendUnit: appendUnit,
  steps: steps,
  transformMap: transformMap
});
var css = function css(element, props) {
  warning(false, 'css() is deprecated, use styler instead');
  return styler(element, props);
};
var svg = function svg(element, props) {
  warning(false, 'svg() is deprecated, use styler instead');
  return styler(element, props);
};
export { action, multicast, value, vectorDecay as decay, index as inertia, keyframes, frame as everyFrame, vectorPhysics as physics, vectorSpring as spring, timeline, tween, listen, index$1 as pointer, mouse, multitouch, chain, composite, crossfade, delay, merge, parallel$1 as parallel, schedule, stagger, calc, transformers as transform, css, svg, Action, ValueReaction };